import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Column, INK, OLIVE, SERIF, BODY } from './DagbladFrontPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };
const WD = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];

function HabitTracker() {
  const [d, setD] = useState(null);
  const [name, setName] = useState('');
  const load = () => axios.get(`${API}/bujo/habits`, cfg).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (h, day) => {
    setD((cur) => ({ ...cur, habits: cur.habits.map((x) => (x.id !== h.id ? x : { ...x, done: x.done.includes(day) ? x.done.filter((y) => y !== day) : [...x.done, day] })) }));
    try { await axios.post(`${API}/bujo/habits/${h.id}/toggle`, { date: day }, cfg); } catch { toast.error('Kunde inte spara'); load(); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await axios.post(`${API}/bujo/habits`, { name: name.trim(), emoji: '🌱' }, cfg); setName(''); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Kunde inte lägga till'); }
  };
  const remove = async (h) => { await axios.delete(`${API}/bujo/habits/${h.id}`, cfg).catch(() => {}); load(); };
  if (!d) return null;
  return (
    <Column title="Vanetracker · veckan" testid="dagblad-habits">
      <div className="grid gap-y-1" style={{ gridTemplateColumns: 'minmax(90px,1fr) repeat(7, 26px) 18px' }}>
        <span />
        {d.days.map((day, i) => <span key={day} className={`text-center text-[11px] font-black ${day === d.today ? 'underline' : ''}`} style={{ color: OLIVE }}>{WD[i]}</span>)}
        <span />
        {d.habits.map((h) => (
          <React.Fragment key={h.id}>
            <span className="text-sm truncate pr-1" style={{ fontFamily: BODY }} data-testid={`habit-name-${h.id}`}>{h.emoji} {h.name}</span>
            {d.days.map((day) => (
              <button key={day} onClick={() => toggle(h, day)} aria-label={`${h.name} ${day}`} data-testid={`habit-${h.id}-${day}`}
                className="w-5 h-5 mx-auto border-2 flex items-center justify-center text-[11px] font-black text-[#F4EEE1]"
                style={{ borderColor: INK, background: h.done.includes(day) ? OLIVE : 'transparent' }}>{h.done.includes(day) ? '✓' : ''}</button>
            ))}
            <button onClick={() => remove(h)} className="no-print text-xs opacity-40 hover:opacity-100" aria-label="Ta bort vana" data-testid={`habit-delete-${h.id}`}>✕</button>
          </React.Fragment>
        ))}
      </div>
      {!d.habits.length && <p className="no-print italic text-sm opacity-70" style={{ fontFamily: BODY }}>Lägg till 1–3 små vanor, till exempel "Dricka vatten".</p>}
      <form onSubmit={add} className="no-print mt-2 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ny vana…" maxLength={60}
          className="flex-1 bg-transparent border-b-2 px-1 py-1 text-sm outline-none" style={{ borderColor: INK, fontFamily: BODY }} data-testid="habit-input" />
        <button type="submit" className="px-3 py-1 text-xs font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: OLIVE }} data-testid="habit-add">Lägg till</button>
      </form>
    </Column>
  );
}

function DistractionTracker() {
  const [d, setD] = useState(null);
  const load = () => axios.get(`${API}/bujo/distractions`, cfg).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const log = async (kind) => {
    try { await axios.post(`${API}/bujo/distractions`, { kind }, cfg); load(); toast('Noterat, tillbaka till det du gjorde 💛'); }
    catch { toast.error('Kunde inte spara'); }
  };
  if (!d) return null;
  const top = d.kinds.find((k) => k.id === d.top_kind);
  const max = Math.max(1, ...Object.values(d.per_day));
  return (
    <Column title="Distraktionstracker" testid="dagblad-distractions">
      <p className="text-sm mb-2" style={{ fontFamily: BODY }}>Blev du distraherad? Tryck och gå tillbaka, ingen skam.</p>
      <div className="no-print flex flex-wrap gap-1.5">
        {d.kinds.map((k) => (
          <button key={k.id} onClick={() => log(k.id)} className="px-2.5 py-1 border-2 text-xs font-bold transition-transform hover:scale-105" style={{ borderColor: INK }} data-testid={`distraction-${k.id}`}>
            {k.emoji} {k.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-sm" style={{ fontFamily: BODY }} data-testid="distraction-today-count">Idag: <b>{d.today.length}</b> gånger{top ? ` · veckans värsta: ${top.emoji} ${top.label}` : ''}</p>
      <div className="flex items-end gap-1 h-10 mt-1" data-testid="distraction-week-bars">
        {Object.entries(d.per_day).map(([day, n]) => (
          <div key={day} className="flex-1" title={`${day}: ${n}`} style={{ height: `${Math.max(6, (n / max) * 100)}%`, background: n ? OLIVE : 'rgba(43,38,32,0.15)' }} />
        ))}
      </div>
    </Column>
  );
}

const BOX = [['Andas in', 'in'], ['Håll', 'hold'], ['Andas ut', 'out'], ['Håll', 'hold2']];

function BoxBreathing() {
  const [on, setOn] = useState(false);
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const id = setInterval(() => setT((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [on]);
  const step = Math.floor(t / 4) % 4;
  const count = 4 - (t % 4);
  const rounds = Math.floor(t / 16);
  const pos = on ? [['0%', '0%'], ['100%', '0%'], ['100%', '100%'], ['0%', '100%']][step] : ['0%', '100%'];
  return (
    <Column title="Boxandning · 4-4-4-4" testid="dagblad-breathing">
      <div className="flex items-center gap-4">
        <div className="relative w-24 h-24 border-[3px] flex-shrink-0" style={{ borderColor: INK }}>
          <span className="absolute w-4 h-4 rounded-full -ml-2 -mt-2 transition-all ease-linear" style={{ left: pos[0], top: pos[1], background: OLIVE, transitionDuration: on ? '4000ms' : '0ms', opacity: on ? 1 : 0.35 }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-black" style={{ fontFamily: SERIF }} data-testid="breathing-count">{on ? count : '□'}</span>
          </div>
        </div>
        <div>
          <p className="font-black text-lg" style={{ fontFamily: SERIF }} data-testid="breathing-phase">{on ? BOX[step][0] : 'Fyrkantsandning'}</p>
          <p className="text-xs" style={{ fontFamily: BODY }}>{on ? `Varv ${rounds + 1}` : 'In 4 · håll 4 · ut 4 · håll 4. Lugnar nervsystemet på en minut.'}</p>
          <button onClick={() => { setOn(!on); setT(0); }} className="no-print mt-2 px-3 py-1 text-xs font-black uppercase tracking-widest text-[#F4EEE1]"
            style={{ background: on ? INK : OLIVE }} data-testid="breathing-toggle">{on ? 'Stoppa' : 'Börja andas'}</button>
        </div>
      </div>
    </Column>
  );
}

function WheelSvg({ areas, ratings, previous }) {
  const c = 90; const r = 70;
  const pt = (i, v) => {
    const a = (Math.PI * 2 * i) / areas.length - Math.PI / 2;
    return [c + Math.cos(a) * r * (v / 10), c + Math.sin(a) * r * (v / 10)];
  };
  const poly = (src) => areas.map((a, i) => pt(i, src[a.id] || 0).join(',')).join(' ');
  return (
    <svg viewBox="-24 -8 228 196" className="w-48 h-48 flex-shrink-0" data-testid="wheel-svg">
      {[2.5, 5, 7.5, 10].map((v) => <polygon key={v} points={areas.map((_, i) => pt(i, v).join(',')).join(' ')} fill="none" stroke="rgba(43,38,32,0.25)" />)}
      {areas.map((a, i) => { const [x, y] = pt(i, 10); return <line key={a.id} x1={c} y1={c} x2={x} y2={y} stroke="rgba(43,38,32,0.2)" />; })}
      {Object.keys(previous).length > 0 && <polygon points={poly(previous)} fill="none" stroke={INK} strokeDasharray="3 3" />}
      <polygon points={poly(ratings)} fill="rgba(107,114,70,0.45)" stroke={OLIVE} strokeWidth="2" />
      {areas.map((a, i) => { const [x, y] = pt(i, 12.2); return <text key={a.id} x={x} y={y} fontSize="10.5" textAnchor="middle" dominantBaseline="middle" fill={INK} fontFamily="Georgia">{a.label.split(' ')[0]}</text>; })}
    </svg>
  );
}

function LifeWheel() {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/bujo/wheel`, cfg).then((r) => {
      const ratings = Object.fromEntries(r.data.areas.map((a) => [a.id, r.data.ratings[a.id] || 5]));
      setD({ ...r.data, ratings });
    }).catch(() => {});
  }, []);
  if (!d) return null;
  const set = (id, v) => setD((cur) => ({ ...cur, ratings: { ...cur.ratings, [id]: v } }));
  const save = () => axios.put(`${API}/bujo/wheel`, { ratings: d.ratings }, cfg).then(() => toast.success('🎡 Livshjulet sparat för veckan')).catch(() => toast.error('Kunde inte spara'));
  return (
    <Column title="Livshjulet · veckans känsla" testid="dagblad-wheel">
      <div className="flex flex-wrap items-center gap-4">
        <WheelSvg areas={d.areas} ratings={d.ratings} previous={d.previous} />
        <div className="flex-1 min-w-[180px] space-y-1">
          {d.areas.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-xs" style={{ fontFamily: BODY }}>
              <span className="w-24 truncate">{a.label}</span>
              <input type="range" min="1" max="10" value={d.ratings[a.id]} onChange={(e) => set(a.id, Number(e.target.value))}
                className="flex-1" style={{ accentColor: OLIVE }} data-testid={`wheel-${a.id}`} />
              <span className="w-5 font-black">{d.ratings[a.id]}</span>
            </label>
          ))}
          <button onClick={save} className="no-print mt-1 px-3 py-1 text-xs font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: OLIVE }} data-testid="wheel-save">Spara veckan</button>
          {Object.keys(d.previous).length > 0 && <p className="text-[10px] italic opacity-70">Streckad linje = förra veckan</p>}
        </div>
      </div>
    </Column>
  );
}

function Journal() {
  const [d, setD] = useState(null);
  const [saved, setSaved] = useState(true);
  useEffect(() => { axios.get(`${API}/bujo/journal`, cfg).then((r) => setD(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await axios.put(`${API}/bujo/journal`, d, cfg); setSaved(true); } catch { toast.error('Kunde inte spara'); }
  };
  if (!d) return null;
  const upd = (patch) => { setD((cur) => ({ ...cur, ...patch })); setSaved(false); };
  return (
    <Column title="Hjärndump och tacksamhet" testid="dagblad-journal">
      <textarea value={d.dump} onChange={(e) => upd({ dump: e.target.value })} onBlur={save} rows={4} placeholder="Töm huvudet här. Allt får plats, inget behöver vara ordnat…"
        className="w-full bg-transparent border-2 p-2 text-sm outline-none resize-y" style={{ borderColor: INK, fontFamily: BODY }} data-testid="journal-dump" />
      <p className="mt-2 text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: OLIVE }}>Tre saker jag är tacksam för</p>
      {d.gratitude.map((g, i) => (
        <input key={i} value={g} onChange={(e) => upd({ gratitude: d.gratitude.map((x, j) => (j === i ? e.target.value : x)) })} onBlur={save}
          placeholder={`${i + 1}.`} maxLength={140} className="w-full bg-transparent border-b border-dotted py-1 text-sm outline-none" style={{ borderColor: INK, fontFamily: BODY }}
          data-testid={`journal-gratitude-${i}`} />
      ))}
      <p className="no-print text-[10px] italic mt-1 opacity-60" data-testid="journal-saved">{saved ? 'Sparat ✓' : 'Sparas när du klickar utanför…'}</p>
    </Column>
  );
}

export function PlannerPages({ on }) {
  const ids = ['habits', 'distractions', 'breathing', 'wheel', 'journal'].filter(on);
  if (!ids.length) return null;
  return (
    <section className="mt-6" data-testid="dagblad-planner">
      <h2 className="text-center text-2xl font-black border-y-[3px] border-double py-1" style={{ fontFamily: SERIF, borderColor: INK }}>Planner-sidan</h2>
      <div className="grid md:grid-cols-2 gap-x-8">
        <div>
          {on('habits') && <HabitTracker />}
          {on('journal') && <Journal />}
        </div>
        <div>
          {on('breathing') && <BoxBreathing />}
          {on('distractions') && <DistractionTracker />}
        </div>
      </div>
      {on('wheel') && <LifeWheel />}
    </section>
  );
}

export function DagbladToolbar({ edition }) {
  const [voices, setVoices] = useState({ warm: true, swedish: false });
  const [voice, setVoice] = useState('warm');
  const [busy, setBusy] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  useEffect(() => { axios.get(`${API}/dagblad/voices`, cfg).then((r) => { setVoices(r.data); if (r.data.swedish) setVoice('swedish'); }).catch(() => {}); }, []);
  useEffect(() => () => { const a = audioRef.current; if (a) { a.pause(); URL.revokeObjectURL(a.src); } }, []);
  const stop = () => {
    const a = audioRef.current;
    if (a) { a.pause(); if (a.src) URL.revokeObjectURL(a.src); audioRef.current = null; }
    setPlaying(false);
  };
  const read = async (mode) => {
    stop();
    setBusy(mode);
    try {
      const res = await axios.post(`${API}/dagblad/read-aloud`, { edition, mode, voice }, { ...cfg, responseType: 'blob', timeout: 120000 });
      const a = new Audio(URL.createObjectURL(res.data));
      audioRef.current = a;
      a.onended = stop;
      try { await a.play(); setPlaying(true); } catch { toast('Tryck en gång till så läser Elsa 🔊'); }
      if (voice === 'swedish' && res.headers['x-voice-used'] !== 'swedish') toast('Den svenska rösten svarade inte, Elsa läser med den varma rösten istället');
    } catch { toast.error('Elsa fick inte fram rösten just nu'); }
    setBusy(null);
  };
  const btn = 'px-3 py-1.5 text-[11px] font-black uppercase tracking-widest border-2 transition-transform hover:scale-105 disabled:opacity-50';
  return (
    <div className="no-print flex flex-wrap items-center justify-center gap-2 pt-3" data-testid="dagblad-toolbar">
      {playing ? (
        <button onClick={stop} className={btn} style={{ borderColor: INK, background: INK, color: '#F4EEE1' }} data-testid="dagblad-read-stop">⏹ Stoppa Elsa</button>
      ) : (
        <>
          <button onClick={() => read('full')} disabled={!!busy} className={btn} style={{ borderColor: INK }} data-testid="dagblad-read-full">{busy === 'full' ? 'Elsa tar på sig glasögonen…' : '🔊 Elsa läser hela'}</button>
          <button onClick={() => read('short')} disabled={!!busy} className={btn} style={{ borderColor: INK }} data-testid="dagblad-read-short">{busy === 'short' ? 'Elsa harklar sig…' : '🔊 Läs kort'}</button>
        </>
      )}
      <select value={voice} onChange={(e) => setVoice(e.target.value)} className="bg-transparent border-2 px-2 py-1 text-[11px] font-bold" style={{ borderColor: INK }} data-testid="dagblad-voice-select">
        <option value="warm">Varm röst</option>
        <option value="swedish" disabled={!voices.swedish}>Svensk röst{voices.swedish ? '' : ' (behöver nyckel)'}</option>
      </select>
      <button onClick={() => window.print()} className={btn} style={{ borderColor: OLIVE, background: OLIVE, color: '#F4EEE1' }} data-testid="dagblad-print">🖨️ Skriv ut / spara PDF</button>
    </div>
  );
}
